import React, { useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import Backdrop from '@material-ui/core/Backdrop';
import styled from 'styled-components';
import Snackbar from '@material-ui/core/Snackbar';
import { useSelector } from 'react-redux';
import ChatContext from 'components/chat/ChatContext';

import acceptCallIcon from 'assets/accept_call.png';
import hangUpCallIcon from 'assets/hangup_call.png';

const Container = styled.div`
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  display: flex;
  height: 100%;
`;

const ImgButton = styled.img`
  cursor: pointer;
`;

const SnackbarContainer = styled.div`
  position: absolute;

  & > div {
    position: relative;

    .MuiSnackbarContent-root {
      min-width: fit-content;
    }
  }
`;

const CallingStatusContainer = styled.div`
  text-align: center;
  color: #FFF;
  margin: ${({ rtcCallingStatus }) => (rtcCallingStatus === 'calling' ? 'unset' : 'auto')};
`;

const RtcContainer = styled.div`
  margin: auto;
  width: 80%;
  position: relative;
  height: 80%;
  text-align: center;

  & > div {
    position: relative;
    height: 100%;
    width: fit-content;
    margin: auto;

    & > video {
      position: relative;
      height: 100%;

      &.isLocal {
        position: absolute;
        width: 300px;
        z-index: 1;
        right: 0;
        height: auto;
      }
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: center;
  color: #FFF;

  & > div {
    margin: 0 100px;
  }
`;

const RtcChatBackdrop = ({
  accept,
  reject,
  hangup,
}) => {
  const { t } = useTranslation();
  const rtcCallingInfo = useSelector((state) => state.rtcCallingInfo);
  const rtcCallingStatus = useSelector((state) => state.rtcCallingStatus);
  const { rtcAlert, setRtcAlert } = useContext(ChatContext);

  return (
    <Backdrop
      style={{ zIndex: 10000, backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      open={rtcCallingStatus !== 'available' || rtcAlert.length > 0}
    >
      <SnackbarContainer>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={rtcAlert.length > 0}
          autoHideDuration={1500}
          onClose={() => setRtcAlert('')}
          message={rtcAlert}
        />
      </SnackbarContainer>
      <Container>
        <CallingStatusContainer
          rtcCallingStatus={rtcCallingStatus}
        >
          {(() => {
            switch (rtcCallingStatus) {
              case 'waiting_for_response':
                return (
                  <Trans
                    i18nKey="call.waiting_response"
                    values={{ name: rtcCallingInfo.rtcTargetName }}
                    // eslint-disable-next-line jsx-a11y/heading-has-content
                    components={{ b: <h2 /> }}
                    defaults="<b>Waiting for response from</b> <b>%{name}</b>"
                  />
                );
              case 'being_called':
                return (
                  <Trans
                    i18nKey="call.invites_you"
                    values={{ name: rtcCallingInfo.rtcTargetName }}
                    // eslint-disable-next-line jsx-a11y/heading-has-content
                    components={{ b: <h2 /> }}
                    defaults="<b>%{name}</b> <b>invites you to video call</b>"
                  />
                );
              default:
                return <h2>{rtcCallingInfo.rtcTargetName}</h2>;
            }
          })}
        </CallingStatusContainer>
        <RtcContainer>
          <div id="rtc-container" />
        </RtcContainer>
        {
          rtcCallingStatus === 'being_called' && (
            <FlexContainer>
              <div role="button">
                <ImgButton src={hangUpCallIcon} alt="Hang Up" onClick={reject} />
                <p>{t('call.hang_up', 'Hang Up')}</p>
              </div>
              <div role="button">
                <ImgButton src={acceptCallIcon} alt="Accept" onClick={accept} />
                <p>{t('call.accept', 'Accept')}</p>
              </div>
            </FlexContainer>
          )
        }
        {
          ['waiting_for_response', 'calling'].includes(rtcCallingStatus) && (
            <FlexContainer>
              <div role="button">
                <ImgButton src={hangUpCallIcon} alt="Hang Up" onClick={hangup} />
                <p>{t('call.hang_up', 'Hang Up')}</p>
              </div>
            </FlexContainer>
          )
        }
      </Container>
    </Backdrop>
  );
};

RtcChatBackdrop.propTypes = {
  accept: PropTypes.func.isRequired,
  reject: PropTypes.func.isRequired,
  hangup: PropTypes.func.isRequired,
};

export default RtcChatBackdrop;
